import api from '../Services/api'
import { Spinner } from 'react-bootstrap';
import { useState, useEffect } from 'react'
import { useParams, useLocation } from "react-router-dom"
import "../CSS/invoice/invoice.css"
import { format } from "../Utilities/Currency";
import InvoiceNote from './InvoiceNote';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { Link } from 'react-router-dom'
import { createInvoicePages, createTable, createTotalTableFooter } from '../Services/InvoiceService';
import InvoiceHeader from './InvoiceHeader';

function createSaleInvoiceTableHeader(pageIndex) {
  return <>
    <thead>
      <tr>
        <th className='checkbox-col'>{pageIndex}</th>
        <th className="text-center px-2">Qty</th>
        <th className='name-col'>
          <div className="name-text px-2">Nama Barang</div>
        </th>
        <th className="price-col text-center">Harga</th>
        <th className="text-center px-2">Disc</th>
        <th className="price-col text-center">Subtotal</th>
      </tr>
    </thead>
  </>
}

function createSaleDetailRow(orderItem, isThisTaxInvoice = false) {
  console.log('Order item structure:', orderItem);
  
  // Use regular price for all invoices
  let basePrice = orderItem.price_per_unit;
    
  let price = basePrice
  let formattedPrice = <div>{format(basePrice)}</div>
  let discount = ''
  

  if (orderItem.discount > 0) {
    price = price - (price * (orderItem.discount / 100))
    const ttkey = `wholesale-tooltip-${orderItem.id}`

    discount = <>
      <OverlayTrigger
        key={ttkey}
        placement="top"
        overlay={
          <Tooltip id={`tooltip-${orderItem.id}`}>
            Minimum pembelian {orderItem.wholesale_minimum_quantity}: diskon {orderItem.discount}%<br />
            <span style={{ textDecoration: "line-through" }}>{format(basePrice)}</span> Rp {format(price)}
          </Tooltip>
        }
      >
        <div>{orderItem.discount}%</div>
      </OverlayTrigger>
    </>
  }

  return (
    <tr key={`invrow-${orderItem.id}`}>
      <td className='checkbox-col'>
        <div className='invoice-checkbox'></div>
      </td>
      <td className='text-center'>{orderItem.pcs}</td>
      <td className='name-col'>
        <div className="name-text">
          {orderItem.product.name}
        </div>
      </td>
      <td className='price-col'>
        <div className='d-flex justify-content-between'>
          <div>Rp</div>
          <div>{formattedPrice}</div>
        </div>
      </td>
      <td className='discount-col text-center'>{-orderItem.discount}</td>
      <td className='price-col text-end'>
        <div className='d-flex justify-content-between'>
          <div>Rp</div>
          <div>{format(price)}</div>
        </div>
      </td>
    </tr>
  )

}

const Invoice = () => {
  const { orderUUID } = useParams();
  const location = useLocation();
  const [orderData, setOrderData] = useState();
  const [relatedOrderData, setRelatedOrderData] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Calculate totals for orders
  const calculateOrderTotal = (order) => {
    let orderTotal = 0
    const isThisTaxInvoice = order.note?.includes('Dengan Pajak');
    
    for (let orderDetail of order.order_items) {
      // Use regular price for all invoices
      let basePrice = orderDetail.price_per_unit;
        
      const finalPrice = basePrice - (basePrice * (orderDetail.discount || 0) / 100);
      const sub = orderDetail.pcs * finalPrice;
      orderTotal += sub
    }
    return orderTotal
  }

  useEffect(() => {
    const fetchMainOrder = api.get(`/data/order/${orderUUID}/`);

    const relatedOrders = location.state?.relatedOrders || [orderUUID];
    const otherOrderUUIDs = relatedOrders.filter(uuid => uuid !== orderUUID);

    const fetchRelatedOrders = otherOrderUUIDs.map(uuid =>
      api.get(`/data/order/${uuid}/`)
    );

    Promise.all([fetchMainOrder, ...fetchRelatedOrders])
      .then(responses => {
        const mainOrder = responses[0].data;
        const relatedOrders = responses.slice(1).map(r => r.data);
        
        setOrderData(mainOrder);
        setRelatedOrderData(relatedOrders);
        
        // Calculate total for main order
        setTotal(calculateOrderTotal(mainOrder))
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching orders:', err);
        setIsLoading(false);
      });
  }, [orderUUID, location.state]);

  if (isLoading) {
    return (
      <Spinner className="spinner" animation="border" role="status" size="xl">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  // If the fetch failed (e.g. expired session handled by the api interceptor, or
  // a network error), orderData is undefined. Render a safe message instead of
  // crashing on orderData.note below.
  if (!orderData) {
    return (
      <div className="d-print-none" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Gagal memuat invoice. Silakan muat ulang halaman atau masuk kembali.</p>
      </div>
    );
  }

  // Helper function to render a single invoice
  const renderInvoice = (order, invoiceTotal, invoiceType = '', showNote = true) => {
    const invoiceHeader = <InvoiceHeader key={`invh-${order.id}`} orderData={order} />
    const isThisTaxInvoice = order.note?.includes('Dengan Pajak');

    const data = createInvoicePages(order.order_items, invoiceHeader, (pageIndex, page, isLastPage) => {
      const thead = createSaleInvoiceTableHeader(pageIndex + 1)
      const tableRows = []
      for (let orderDetail of page) {
        tableRows.push(createSaleDetailRow(orderDetail, isThisTaxInvoice))
      }
      const tfoot = isLastPage ? createTotalTableFooter(invoiceTotal) : null
      return createTable(thead, tableRows, tfoot)
    })

    return (
      <div key={`invoice-${order.id}`} className="invoice-section">
        {invoiceType && (
          <div className="invoice-type-header d-print-none mb-3">
            <h3 className="text-center">{invoiceType}</h3>
          </div>
        )}
        
        {Object.keys(data.pages).map((pageNumber, index) => {
          return data.pages[pageNumber]
        })}

        {showNote && <InvoiceNote total={invoiceTotal} orderData={order} />}
        
        {relatedOrderData.length > 0 && (
          <div className="page-break" style={{pageBreakAfter: 'always'}}></div>
        )}
      </div>
    )
  }


  return (
    <>
      <div className="container px-0 invoice-actions-container d-print-none" style={{ 'maxWidth': '850px' }}>
        <Link 
          className='btn action-button mx-2 d-print-none' 
          to="t"
          state={{ relatedOrders: relatedOrderData.length > 0 ? [orderUUID, ...relatedOrderData.map(o => o.uuid)] : [orderUUID] }}
        >
          Thermal
        </Link>
        <button className='btn action-button mx-2 d-print-none' onClick={window.print}>Print All</button>
        {relatedOrderData.length > 0 && (
          <div className="alert alert-info mt-2">
            <strong>📄 Invoices terpisah:</strong> Produk dengan pajak dan tanpa pajak telah dipisahkan menjadi {relatedOrderData.length + 1} invoice berbeda.
          </div>
        )}
      </div>

      {/* Main Invoice */}
      {renderInvoice(
        orderData, 
        total, 
        relatedOrderData.length > 0 ? (orderData.note?.includes('Dengan Pajak') ? 'INVOICE DENGAN PAJAK' : 'INVOICE TANPA PAJAK') : '',
        relatedOrderData.length === 0 // Show note only if no related invoices
      )}

      {/* Related Invoices */}
      {relatedOrderData.map((relatedOrder, index) => {
        const relatedTotal = calculateOrderTotal(relatedOrder);
        const invoiceType = relatedOrder.note?.includes('Dengan Pajak') ? 'INVOICE DENGAN PAJAK' : 'INVOICE TANPA PAJAK';
        const isLastInvoice = index === relatedOrderData.length - 1;
        return renderInvoice(relatedOrder, relatedTotal, invoiceType, isLastInvoice);
      })}
    </>
  )
}

export default Invoice
